/**
 * LanguageSelector component for selecting transcription language
 * Requirements: 8.1, 8.2, 8.3
 */

import React from 'react';
import {
  LanguageOption,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
} from '../types';

export interface LanguageSelectorProps {
  /** Currently selected language */
  selectedLanguage: LanguageOption;
  /** Callback when language selection changes */
  onLanguageChange: (language: LanguageOption) => void;
  /** Whether the selector is disabled (e.g., during active recording) */
  disabled?: boolean;
}

/**
 * Dropdown component for selecting transcription language.
 * Displays English, French, and Korean options.
 * Defaults to English when no selection is made.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  selectedLanguage,
  onLanguageChange,
  disabled = false,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCode = event.target.value;
    const language = SUPPORTED_LANGUAGES.find(
      (lang) => lang.code === selectedCode
    );
    if (language) {
      onLanguageChange(language);
    }
  };

  return (
    <div className="language-selector">
      <label htmlFor="language-select" className="language-selector__label">
        Language:
      </label>
      <select
        id="language-select"
        className="language-selector__select"
        value={selectedLanguage.code}
        onChange={handleChange}
        disabled={disabled}
        aria-label="Select transcription language"
      >
        {SUPPORTED_LANGUAGES.map((language) => (
          <option key={language.code} value={language.code}>
            {language.name}
          </option>
        ))}
      </select>
      <span className="language-selector__current">
        ({selectedLanguage.transcribeCode})
      </span>
    </div>
  );
};

export default LanguageSelector;
